import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Phone number is required to continue'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/onboarding`, { phone: phone.trim() }, { withCredentials: true });
      toast.success('Welcome to KarmaCircle!');
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#110A06] flex items-center justify-center px-4 relative">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-[#D4A54A]/5 blur-[120px]" />
      <div className="w-full max-w-md relative z-10" data-testid="onboarding-page">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="KarmaCircle" className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4A54A]/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#F5E6C8]">Almost There!</h1>
          <p className="font-sans text-sm text-[#A89070] mt-2">
            Welcome, <span className="text-[#D4A54A] font-medium">{user?.name}</span>! We just need your phone number to complete your profile.
          </p>
        </div>

        <div className="bg-[#1A1008] rounded-3xl border border-[#3D2D1D]/50 p-8">
          {error && (
            <div data-testid="onboarding-error" className="bg-[#C0392B]/10 border border-[#C0392B]/20 text-[#E07A5F] rounded-xl px-4 py-3 mb-4 font-sans text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Phone Number *
              </Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                data-testid="onboarding-phone-input"
                className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-12 focus:border-[#D4A54A] placeholder:text-[#5C4830] text-lg"
                placeholder="+91 9876543210"
                autoFocus
              />
              <p className="font-sans text-xs text-[#A89070] mt-2">We'll use this for WhatsApp updates and event reminders</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="onboarding-submit-btn"
              className="w-full bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-3.5 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              Continue to Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
